import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet";
import { Typography, Grid, Container, Box } from "@mui/material";
import api from "../../globals/axios";
import CustomAppBar from "../../components/AppBar";
import { getUserDetails } from "../../globals/authentication";
import { getAreaInAcres } from "../../globals/utils";
import { makeStyles } from "@mui/styles";
import { useLocation } from "react-router-dom";
import CustomButton from "../../components/Custom/CustomButton";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import useFormValidation from "../../components/Custom/FormValidation";
import { farmValidationSchema } from "../../components/Custom/ValidationSchema";
const validationSchema = farmValidationSchema();
import { farmFields } from "../../Data/fieldData";
import CustomTextField from "../../components/Custom/CustomTextField";
import { useAlert } from "../../components/Alert";
import FileUpload from "../../components/Custom/FileUpload";
import LoaderContainer from "../../components/LoaderContainer";
import { compressImage } from "../../globals/utils";
import FarmMapModal from "../../components/FarmMapModal";
import LocateLand from "../../components/LocateLand";
import { LoadScript } from "@react-google-maps/api";
import { GOOGLE_MAPS_API_KEY } from "../../globals/const";
import { calculatePolygonCenter } from "../../utils/mapUtils";

const useStyles = makeStyles(() => ({
  title: {
    fontSize: "30px !important",

    fontWeight: "700 !important",
    lineHeight: "18px",
  },
  value: {
    fontSize: "12px !important",
    color: "#827F7F",
    fontWeight: "400 !important",
    lineHeight: "18px",
  },
  fileText: {
    fontSize: "15px !important",
    // color: "#827F7F",
    fontWeight: "700 !important",
    lineHeight: "18px",
    marginTop: "8px",
  },
  mapPreview: {
    width: "100%",
    height: "200px",
    marginTop: "16px",
    border: "1px solid #ccc",
    borderRadius: "4px",
    overflow: "hidden",
  },
}));
export default function CreateFarm() {
  const classes = useStyles();
  const [formData, setFormData] = useState({});
  const [location, setLocation] = useState({});
  const [, setCurrentLocation] = useState(null);
  const [mapLoaded, setMapLoaded] = useState(false);
  const locationd = useLocation();
  const [path, setPath] = useState([]);
  const [center, setCenter] = useState({});
  const [userCenter, setUserCenter] = useState({});
  const [isRecentered, setIsRecentered] = useState(false);
  const { t } = useTranslation();
  const [isEdit, setIsEdit] = useState(false);
  const navigate = useNavigate();
  const searchParams = new URLSearchParams(locationd.search);
  const farmId = searchParams.get("farmId");
  const { showAlert } = useAlert();
  const { errors, validate, validateField } = useFormValidation(
    formData,
    validationSchema
  );
  const [loading, setLoading] = useState(true);
  const [irrigationSourceData, setIrrigationSourceData] = useState([]);
  const [irrigationSystemData, setIrrigationSystemData] = useState([]);
  const [soilTexture, setSoilTexture] = useState([]);
  const [ownerType, setOwnerType] = useState([]);
  const [ownerTypeName, setOwnerTypeName] = useState("");
  const [mapModalOpen, setMapModalOpen] = useState(false);
  const [isScriptLoaded, setIsScriptLoaded] = useState(false);
  const DEFAULT_LOCATION = { latitude: 22.862299, longitude: 77.604459 };
  const delta = 0.0006;

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [source, system, soil, owner] = await Promise.all([
          api.get(`farmer/irrigation/?type=source`),
          api.get(`farmer/irrigation/?type=system`),
          api.get(`farmer/irrigation/?type=soil-texture`),
          api.get(`farmer/irrigation/?type=owner-type`),
        ]);
        setIrrigationSourceData(source.data);
        setIrrigationSystemData(system.data);
        setSoilTexture(soil.data);
        setOwnerType(owner.data);
      } catch (error) {
        console.error("Error fetching data", error);
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    const userDetails = getUserDetails();
    setFormData((prevState) => ({
      ...prevState,
      owner_name: userDetails?.first_name,
    }));
  }, []);
  const recenter = () => {
    setIsRecentered(true);
    setLocation({
      latitude: userCenter.lat,
      longitude: userCenter.lng,
    });
  };

  useEffect(() => {
    // Check if in edit mode
    if (farmId) {
      fetchFarmData(farmId);
      setIsEdit(true);
      getMyLocation();
    } else {
      getMyLocation();
      setLoading(false);
      const intervalId = setInterval(() => {
        if (!isRecentered) {
          // getMyLocation();
        }
      }, 5000);

      return () => clearInterval(intervalId);
    }
  }, [isEdit, isRecentered]);

  const fetchFarmData = async (farmId) => {
    try {
      const response = await api.get(`/farmer/farms/${farmId}/`);
      const farmData = response.data || {};

      const formatOptionsArray = (options) =>
        (options || []).map(({ id }) => id);
      const formatOptionsId = (options) => options?.[0]?.id || "";

      setFormData({
        owner_name: farmData.owner_name || "",
        area: farmData.area || "",
        farm_name: farmData.farm_name || "",
        regulator: farmData.regulator || "",
        land_record_number: farmData.land_record_number || "",
        pin_code: farmData.pin_code || "",
        irrigation_source: formatOptionsArray(farmData.irrigation_source),
        irrigation_system: formatOptionsArray(farmData.irrigation_system),
        soil_texture: formatOptionsId(farmData.soil_texture),
        owner_type: formatOptionsId(farmData.owner_type),
        land_doc: "",
        polygon: farmData.polygon || [],
      });

      const polygon = farmData.polygon || [];
      setPath(polygon);

      if (polygon.length > 0) {
        const polygonCenter = calculatePolygonCenter(polygon);
        if (polygonCenter) {
          setCenter(polygonCenter);
          setUserCenter(polygonCenter);
        }
      }

      setMapLoaded(!!(polygon.length > 0 && farmData.area));
    } catch (error) {
      console.error("Error fetching farm data:", error);
    } finally {
      setLoading(false);
    }
  };

  const isPointInDefaultPolygon = () => {
    const minLat = DEFAULT_LOCATION.latitude - delta;
    const maxLat = DEFAULT_LOCATION.latitude + delta;
    const minLng = DEFAULT_LOCATION.longitude - delta;
    const maxLng = DEFAULT_LOCATION.longitude + delta;

    return (
      center.lat >= minLat &&
      center.lat <= maxLat &&
      center.lng >= minLng &&
      center.lng <= maxLng
    );
  };

  const createFarm = async () => {
    setLoading(true);
    try {
      const validationErrors = await validate(formData);
      if (Object.keys(validationErrors).length === 0) {
        const isPolygonAtDefault = isPointInDefaultPolygon();

        if (isPolygonAtDefault) {
          showAlert(
            "warning",
            t("You are not at your farm. Please go there to map it.")
          );
          setLoading(false);
          return;
        }

        if (formData.area > 20) {
          showAlert(
            "warning",
            t(
              "Farm area exceeds 20 acres. Please contact support to proceed further."
            )
          );
          return;
        }

        const fileFields = ["sowing_file"];
        const _formData = new FormData();

        for (const key in formData) {
          if (formData[key] instanceof FileList) {
            if (key === "owner_type" && Array.isArray(formData[key])) {
              _formData.append(key, formData[key][0]); // Assuming owner_type is a single selection
            } else {
              _formData.append(key, formData[key]);
            }
          } else {
            if (fileFields.includes(key) && typeof formData[key] === "string") {
              continue;
            }
            if (key === "polygon" && Array.isArray(formData[key])) {
              _formData.append(key, JSON.stringify(formData[key]));
            } else if (key === "land_doc" && formData[key] === "") {
              continue;
            } else if (Array.isArray(formData[key])) {
              const formattedArray = formData[key].map((item) =>
                parseInt(item, 10)
              );
              _formData.append(key, JSON.stringify(formattedArray));
            } else {
              _formData.append(key, formData[key]);
            }
          }
        }

        if (isEdit) {
          _formData.append("id", farmId);
        }
        if (!isEdit) {
          _formData.append("polygon", JSON.stringify(path));
        }

        const apiRequest = isEdit
          ? api.patch(`/farmer/farms/${farmId}/`, _formData)
          : api.post("/farmer/farms/", _formData);

        const response = await apiRequest;
        if (response && response.data) {
          showAlert(
            "success",
            isEdit ? "Updated Successfully" : "Created Successfully"
          );

          if (isEdit) navigate("/");
          else navigate(`/create-crop?farmId=${response.data.id}`);
        }
      } else {
        showAlert(
          "warning",
          "Please fix the validation errors before submitting."
        );
      }
    } catch (error) {
      let errorMessage = error.response?.data?.message || "An error occured";
      showAlert("error", errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const addVertex = () => {
    if (
      typeof location.latitude === "number" &&
      typeof location.longitude === "number"
    ) {
      const vertex = { lat: location.latitude, lng: location.longitude };
      const newPath = [...path, vertex];
      setPath(newPath);
    }
  };

  useEffect(() => {
    const _area = getAreaInAcres(path).toFixed(2);
    setFormData((prevData) => ({ ...prevData, area: _area }));
  }, [path]);

  const resetMap = () => {
    if (
      typeof location.latitude === "number" &&
      typeof location.longitude === "number"
    ) {
      const _center = { lat: location.latitude, lng: location.longitude };
      setPath([_center]);
      setCenter(_center);
      setUserCenter(_center);
    }
  };

  const getMyLocation = () => {
    const location = window.navigator && window.navigator.geolocation;

    if (location) {
      location.watchPosition(
        (position) => {
          setLocation({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            accuracy: position.coords.accuracy,
          });
        },
        (error) => {
          console.error("Error getting location:", error);
          setLocation(DEFAULT_LOCATION);
        },
        {
          enableHighAccuracy: true,
          maximumAge: 0,
          timeout: 5000,
        }
      );
    } else {
      setLocation(DEFAULT_LOCATION);
      alert("Could not get location. Manually drag the map and recenter plot.");
    }
  };

  useEffect(() => {
    if (location?.latitude && !mapLoaded) {
      setMapLoaded(true);
      renderRectangle(location["latitude"], location["longitude"]);
    }
  }, [location]);
  useEffect(() => {
    if (isEdit && location.latitude && location.longitude) {
      const newArea = getAreaInAcres(path).toFixed(2);
      setFormData((prevData) => ({
        ...prevData,
        area: newArea,
      }));
      setFormData((prevData) => ({
        ...prevData,
        polygon: path,
      }));
    }
  }, [location, path, isEdit]);

  const renderRectangle = (latitude, longitude) => {
    const mapcenter = {
      lat: location["latitude"],
      lng: location["longitude"],
    };

    setCenter(mapcenter);

    const rectangle = [{ lat: latitude, lng: longitude }];
    setPath(rectangle);
  };

  const handleChange = (name, value) => {
    setFormData((prevData) => ({ ...prevData, [name]: value }));
    validateField(name, value);
  };

  const onFileChange = async (event) => {
    const file = event.target.files[0];
    if (!file) {
      return;
    }

    try {
      const compressedFile = await compressImage(file);
      setFormData((prevData) => ({
        ...prevData,
        [event.target.name]: compressedFile,
      }));
    } catch (error) {
      console.error("Error compressing image:", error);
      showAlert(
        "error",
        "An error occurred while compressing the image. Please try again."
      );
    }
  };

  const handleAddFarm = () => {
    if (location.latitude && location.longitude) {
      const newCenter = { lat: location.latitude, lng: location.longitude };
      const newPolygon = [
        { lat: newCenter.lat + delta, lng: newCenter.lng + delta },
        { lat: newCenter.lat + delta, lng: newCenter.lng - delta },
        { lat: newCenter.lat - delta, lng: newCenter.lng - delta },
        { lat: newCenter.lat - delta, lng: newCenter.lng + delta },
      ];
      setPath(newPolygon);
      setCenter(newCenter);
      setUserCenter(newCenter);
    } else {
      console.log("Location coordinates are not available");
    }
  };

  const handleMapModalClose = (savedCenter) => {
    setMapModalOpen(false);

    // Only update center if we have a saved center from the modal
    if (savedCenter) {
      setCenter(savedCenter);
      setUserCenter(savedCenter);
    }

    // Don't reset location state to current GPS - preserve modal state
    // This allows the boundary coordinates to be maintained
  };

  return (
    <>
      <Helmet>
        <title>{!isEdit ? t("Create Farm") : t("Update Farm")} - Farmapp</title>
      </Helmet>
      <CustomAppBar />
      <LoaderContainer loading={loading}>
        <Container maxWidth="xs" sx={{ mb: 3, marginTop: "20px" }}>
          <div>
            <Grid
              container
              // spacing={1}
              justify="center"
              direction="column"
              style={{ marginTop: "10px" }}
            >
              <Typography className={classes.title}>
                {!isEdit ? t("Create Farm") : t("Update Farm")}
              </Typography>

              {farmFields.map((field) => (
                <React.Fragment key={field.name}>
                  {field.type === "text" && (
                    <CustomTextField
                      label={t(field.label)}
                      name={field.name}
                      value={formData[field.name] || ""}
                      onChange={handleChange}
                      type={field.type}
                      error={!!errors[field.name]}
                      helperText={errors[field.name]}
                      required={field.required}
                    />
                  )}
                  {field.type === "number" && (
                    <CustomTextField
                      label={t(field.label)}
                      name={field.name}
                      value={formData[field.name] || ""}
                      onChange={handleChange}
                      type={field.type}
                      error={!!errors[field.name]}
                      helperText={errors[field.name]}
                      required={field.required}
                    />
                  )}
                  {field.type === "select" && field.name === "soil_texture" && (
                    <CustomTextField
                      label={t(field.label)}
                      name={field.name}
                      value={formData[field.name] || ""}
                      onChange={handleChange}
                      type={field.type}
                      options={soilTexture.map((source) => ({
                        value: source.id,
                        label: source.name,
                      }))}
                      error={!!errors[field.name]}
                      helperText={errors[field.name]}
                      required={field.required}
                    />
                  )}
                  {field.type === "select" && field.name === "owner_type" && (
                    <CustomTextField
                      label={t(field.label)}
                      name={field.name}
                      value={formData[field.name] || ""}
                      onChange={handleChange}
                      type={field.type}
                      options={ownerType.map((source) => ({
                        value: source.id,
                        label: source.name,
                      }))}
                      error={!!errors[field.name]}
                      helperText={errors[field.name]}
                      onClick={(label) => setOwnerTypeName(label)}
                      required={field.required}
                    />
                  )}
                  {field.type === "checkboxes" &&
                    field.name === "irrigation_source" && (
                      <CustomTextField
                        label={t(field.label)}
                        name={field.name}
                        value={formData[field.name] || ""}
                        onChange={handleChange}
                        type={field.type}
                        options={irrigationSourceData.map((source) => ({
                          value: source.id,
                          label: source.name,
                        }))}
                        error={!!errors[field.name]}
                        helperText={errors[field.name]}
                        required={field.required}
                      />
                    )}
                  {field.type === "checkboxes" &&
                    field.name === "irrigation_system" && (
                      <CustomTextField
                        label={t(field.label)}
                        name={field.name}
                        value={formData[field.name] || ""}
                        onChange={handleChange}
                        type={field.type}
                        options={irrigationSystemData.map((source) => ({
                          value: source.id,
                          label: source.name,
                        }))}
                        error={!!errors[field.name]}
                        helperText={errors[field.name]}
                        required={field.required}
                      />
                    )}
                  {field.type === "file" && (
                    <FileUpload
                      label={t(
                        field.name === "land_doc" ? ownerTypeName : field.label
                      )}
                      onChange={onFileChange}
                      name={field.name}
                      accept={field.accept}
                      error={!!errors[field.name]}
                      helperText={errors[field.name]}
                      edit={false} // assuming it's not edit mode
                      disabled={!formData.owner_type}
                      documentText={field.name === "land_doc" ? "document" : ""}
                    />
                  )}
                  {field.type === "map" && (
                    <>
                      <LoadScript
                        googleMapsApiKey={GOOGLE_MAPS_API_KEY}
                        onLoad={() => setIsScriptLoaded(true)}
                      >
                        {path.length > 3 && (
                          <Box className={classes.mapPreview}>
                            <LocateLand
                              center={center}
                              setCenter={() => {}}
                              path={path}
                              setPath={() => {}}
                              editable={false}
                              setCurrentLocation={() => {}}
                              mapHeight="100%"
                              isScriptLoaded={isScriptLoaded}
                            />
                          </Box>
                        )}

                        <CustomButton
                          hideIcon
                          onClick={() => setMapModalOpen(true)}
                        >
                          {path.length > 1
                            ? t("Edit your Farm")
                            : t("Map your Farm")}
                        </CustomButton>
                        <FarmMapModal
                          open={mapModalOpen}
                          handleClose={handleMapModalClose}
                          center={center}
                          path={path}
                          setPath={setPath}
                          setUserCenter={setUserCenter}
                          location={location}
                          setCurrentLocation={setCurrentLocation}
                          addVertex={addVertex}
                          recenter={recenter}
                          handleAddFarm={handleAddFarm}
                          resetMap={resetMap}
                          userCenter={userCenter}
                          errors={errors}
                          t={t}
                          isScriptLoaded={isScriptLoaded}
                          isEdit={isEdit}
                        />
                      </LoadScript>
                    </>
                  )}
                </React.Fragment>
              ))}
              <CustomButton onClick={createFarm}>
                {isEdit ? t("Save") : t("Create Farm")}
              </CustomButton>
            </Grid>
          </div>
        </Container>
      </LoaderContainer>
    </>
  );
}
